import { useEffect, useMemo, useState } from "react";
import { Plus, Users2, Trash2, Upload, Download, Search, Mail, Phone, MapPin, Wallet, CalendarDays, Cake, FileBadge, Banknote, KeyRound, Copy, Check, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import { ROLE_LABELS } from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY_FORM = {
    name: "",
    role: "cleaner",
    zone: "",
    phone: "",
    email: "",
    skills: "",
    weekly_hours: 40,
    active: true,
    dni: "",
    iban: "",
    address: "",
    hire_date: "",
    contract_type: "",
    birthday: "",
    termination_date: "",
    gross_salary_eur: 0,
    notes: "",
    restricted_property_ids: [],
};

const eur = (n) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

export default function Staff() {
    const { user } = useAuth();
    const canSeeFinancial = ["super_admin", "admin", "coordinator"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalEmp, setModalEmp] = useState(null); // null = closed; {} = new; {id...} = edit
    const [importOpen, setImportOpen] = useState(false);
    const [filter, setFilter] = useState("active");
    const [search, setSearch] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/employees").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const filtered = useMemo(() => {
        return items.filter((e) => {
            if (filter === "active" && !e.active) return false;
            if (filter === "inactive" && e.active) return false;
            if (search) {
                const s = search.toLowerCase();
                if (
                    !(e.name || "").toLowerCase().includes(s) &&
                    !(e.email || "").toLowerCase().includes(s) &&
                    !(e.dni || "").toLowerCase().includes(s) &&
                    !(e.zone || "").toLowerCase().includes(s)
                ) return false;
            }
            return true;
        });
    }, [items, filter, search]);

    const stats = useMemo(() => {
        const actives = items.filter((e) => e.active);
        const total_salary = actives.reduce((acc, e) => acc + (Number(e.gross_salary_eur) || 0), 0);
        return { active: actives.length, inactive: items.length - actives.length, payroll: total_salary };
    }, [items]);

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar empleado? Esta acción es definitiva.")) return;
        await api.delete(`/employees/${id}`);
        load();
    };

    return (
        <div className="space-y-6" data-testid="staff-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Equipo operativo
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">Personal</h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        {stats.active} activos · {stats.inactive} de baja
                        {canSeeFinancial && (
                            <>
                                {" · Nómina bruta mensual: "}
                                <span className="font-display font-semibold text-zentra-ink">{eur(stats.payroll)}</span>
                            </>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setImportOpen(true)}
                        data-testid="import-employees-button"
                    >
                        <Upload size={14} /> Importar Excel
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-primary"
                        onClick={() => setModalEmp({})}
                        data-testid="new-employee-button"
                    >
                        <Plus size={16} /> Nuevo empleado
                    </button>
                </div>
            </div>

            <div className="zentra-card-static p-3 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 bg-zentra-bg border border-zentra-border rounded-md text-sm flex-1 min-w-[220px]">
                    <Search size={14} className="text-zentra-muted" />
                    <input
                        className="bg-transparent outline-none border-0 flex-1 text-sm"
                        placeholder="Buscar por nombre, email, DNI, zona…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="staff-search"
                    />
                </div>
                <div className="flex items-center gap-1.5">
                    {[
                        ["active", `Activos (${stats.active})`],
                        ["inactive", `Bajas (${stats.inactive})`],
                        ["all", "Todos"],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                                filter === k
                                    ? "bg-zentra-primary text-white border-zentra-primary"
                                    : "bg-white text-zentra-ink border-zentra-border"
                            }`}
                            data-testid={`staff-filter-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="zentra-skeleton h-44" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={Users2} title="Sin empleados" description="Añade tu primer operario o técnico, o importa desde Excel." />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-grid">
                    {filtered.map((e) => (
                        <button
                            type="button"
                            key={e.id}
                            onClick={() => setModalEmp(e)}
                            className="zentra-card p-5 text-left"
                            data-testid={`employee-card-${e.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className="w-12 h-12 rounded-lg text-white font-display font-bold flex items-center justify-center text-lg"
                                    style={{ background: e.color || "#0d2419" }}
                                >
                                    {e.name.slice(0, 1).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-display font-semibold text-zentra-ink truncate">{e.name}</p>
                                        {!e.active && (
                                            <span className="zentra-chip bg-zinc-100 text-zinc-700 border-zinc-300 text-[10px]">
                                                BAJA
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-zentra-muted tracking-wider uppercase mt-0.5">
                                        {ROLE_LABELS[e.role] || e.role}
                                        {e.contract_type ? ` · ${e.contract_type}` : ""}
                                    </p>
                                </div>
                                <span
                                    onClick={(ev) => { ev.stopPropagation(); remove(e.id); }}
                                    className="text-zentra-muted hover:text-red-600 cursor-pointer p-1 -m-1"
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Trash2 size={14} />
                                </span>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                                <Item label="Horas/sem" value={`${e.weekly_hours ?? 40}h`} />
                                {canSeeFinancial && e.gross_salary_eur > 0 && <Item label="Bruto/mes" value={eur(e.gross_salary_eur)} />}
                                {e.zone && <Item label="Zona" value={e.zone} />}
                                {canSeeFinancial && e.dni && <Item label="DNI" value={e.dni} mono />}
                                {e.hire_date && <Item label="Alta" value={e.hire_date} mono />}
                                {e.termination_date && <Item label="Baja" value={e.termination_date} mono />}
                            </div>
                            {(e.email || e.phone) && (
                                <div className="mt-3 pt-3 border-t border-zentra-border space-y-1 text-xs">
                                    {e.email && (
                                        <p className="flex items-center gap-1.5 text-zentra-muted truncate">
                                            <Mail size={11} /> <span className="truncate">{e.email}</span>
                                        </p>
                                    )}
                                    {e.phone && (
                                        <p className="flex items-center gap-1.5 text-zentra-muted">
                                            <Phone size={11} /> <span className="font-mono-zentra">{e.phone}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            {e.skills?.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {e.skills.slice(0, 4).map((sk) => (
                                        <span key={sk} className="zentra-chip bg-zentra-bg text-zentra-ink border-zentra-border text-[10px]">
                                            {sk}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            <EmployeeFormModal
                emp={modalEmp}
                canSeeFinancial={canSeeFinancial}
                onClose={() => setModalEmp(null)}
                onSaved={() => { setModalEmp(null); load(); }}
            />

            <ImportEmployeesModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
        </div>
    );
}

function Item({ label, value, mono }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider text-zentra-muted">{label}</p>
            <p className={`text-zentra-ink ${mono ? "font-mono-zentra" : "font-medium"}`}>{value}</p>
        </div>
    );
}

function EmployeeFormModal({ emp, onClose, onSaved, canSeeFinancial = true }) {
    const isEdit = !!(emp && emp.id);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [loginUsers, setLoginUsers] = useState([]);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [restrictionSearch, setRestrictionSearch] = useState("");

    useEffect(() => {
        if (!emp) return;
        if (isEdit) {
            setForm({
                name: emp.name || "",
                role: emp.role || "cleaner",
                zone: emp.zone || "",
                phone: emp.phone || "",
                email: emp.email || "",
                skills: (emp.skills || []).join(", "),
                weekly_hours: emp.weekly_hours ?? 40,
                active: emp.active !== false,
                dni: emp.dni || "",
                iban: emp.iban || "",
                address: emp.address || "",
                hire_date: emp.hire_date || "",
                contract_type: emp.contract_type || "",
                birthday: emp.birthday || "",
                termination_date: emp.termination_date || "",
                gross_salary_eur: emp.gross_salary_eur ?? 0,
                notes: emp.notes || "",
                restricted_property_ids: emp.restricted_property_ids || [],
            });
            // Load existing login users
            api.get(`/employees/${emp.id}/login-users`).then((r) => setLoginUsers(r.data || [])).catch(() => {});
        } else {
            setForm(EMPTY_FORM);
            setLoginUsers([]);
        }
        setError("");
        api.get("/properties").then((r) => setPropertyOptions(r.data || [])).catch(() => setPropertyOptions([]));
    }, [emp, isEdit]);

    if (!emp) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                weekly_hours: Number(form.weekly_hours) || 40,
                gross_salary_eur: Number(form.gross_salary_eur) || 0,
                skills: typeof form.skills === "string"
                    ? form.skills.split(",").map((s) => s.trim()).filter(Boolean)
                    : form.skills,
                hire_date: form.hire_date || null,
                birthday: form.birthday || null,
                termination_date: form.termination_date || null,
            };
            if (isEdit) {
                await api.patch(`/employees/${emp.id}`, payload);
            } else {
                await api.post("/employees", payload);
            }
            onSaved();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={!!emp}
            onClose={onClose}
            title={isEdit ? `Editar · ${emp.name}` : "Nuevo empleado"}
            subtitle={isEdit ? "Modifica cualquier dato. Se guarda al pulsar Actualizar." : "Crea una ficha completa de empleado."}
            wide
            testId="employee-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                    <button type="submit" form="emp-form" className="zentra-btn-primary" disabled={saving} data-testid="save-employee">
                        {saving ? "Guardando…" : isEdit ? "Actualizar" : "Crear"}
                    </button>
                </>
            }
        >
            <form id="emp-form" onSubmit={submit} className="space-y-5">
                <Section icon={Users2} title="Identidad y rol">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="zentra-label">Nombre completo</label>
                            <input className="zentra-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" />
                        </div>
                        {canSeeFinancial && (
                        <div>
                            <label className="zentra-label">DNI / NIE</label>
                            <input className="zentra-input font-mono-zentra" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} placeholder="12345678A" />
                        </div>
                        )}
                        <div>
                            <label className="zentra-label">Rol</label>
                            <select className="zentra-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                                    <option key={k} value={k}>{v}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="zentra-label">Zona</label>
                            <input className="zentra-input" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label flex items-center gap-2">
                                Estado
                            </label>
                            <select className="zentra-select" value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                                <option value="1">Activo</option>
                                <option value="0">De baja</option>
                            </select>
                        </div>
                    </div>
                </Section>

                <Section icon={Phone} title="Contacto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="zentra-label">Email</label>
                            <input type="email" className="zentra-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Teléfono</label>
                            <input className="zentra-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label flex items-center gap-1.5"><MapPin size={12} /> Dirección</label>
                            <input className="zentra-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                        </div>
                    </div>
                </Section>

                <Section icon={FileBadge} title="Contrato y jornada">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="zentra-label">Tipo de contrato</label>
                            <input className="zentra-input" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} placeholder="INDEFINIDO / TEMPORAL / FIJO DISCONTINUO" />
                        </div>
                        <div>
                            <label className="zentra-label">Horas semanales</label>
                            <input type="number" min="0" step="0.5" className="zentra-input" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })} data-testid="employee-weekly-hours" />
                        </div>
                        <div>
                            <label className="zentra-label flex items-center gap-1.5"><CalendarDays size={12} /> Fecha de alta</label>
                            <input type="date" className="zentra-input" value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label">Fecha de baja</label>
                            <input type="date" className="zentra-input" value={form.termination_date || ""} onChange={(e) => setForm({ ...form, termination_date: e.target.value })} />
                        </div>
                        <div>
                            <label className="zentra-label flex items-center gap-1.5"><Cake size={12} /> Cumpleaños</label>
                            <input type="date" className="zentra-input" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label">Habilidades (separadas por coma)</label>
                            <input className="zentra-input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="limpieza profunda, fontanería" />
                        </div>
                    </div>
                </Section>

                <Section icon={Ban} title="Limitaciones de viviendas">
                    <p className="text-xs text-zentra-muted mb-3">Las viviendas seleccionadas no podrán asignarse a este empleado, ni individualmente ni en lote.</p>
                    <input className="zentra-input mb-3" value={restrictionSearch} onChange={(event) => setRestrictionSearch(event.target.value)} placeholder="Buscar vivienda por código o nombre" data-testid="employee-restriction-search" />
                    <div className="max-h-48 overflow-y-auto divide-y divide-zentra-border border border-zentra-border rounded-md">
                        {propertyOptions.filter((property) => {
                            const query = restrictionSearch.trim().toLowerCase();
                            return !query || `${property.code || ""} ${property.name || ""} ${property.city || ""}`.toLowerCase().includes(query);
                        }).map((property) => {
                            const selected = (form.restricted_property_ids || []).includes(property.id);
                            return (
                                <label key={property.id} className={`flex items-center gap-2 p-2 text-xs cursor-pointer ${selected ? "bg-red-50" : "hover:bg-zentra-bg/40"}`}>
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => setForm((current) => ({
                                            ...current,
                                            restricted_property_ids: selected
                                                ? current.restricted_property_ids.filter((id) => id !== property.id)
                                                : [...(current.restricted_property_ids || []), property.id],
                                        }))}
                                        data-testid={`employee-restriction-${property.id}`}
                                    />
                                    <span className="font-mono-zentra text-zentra-muted w-20 shrink-0">{property.code}</span>
                                    <span className="text-zentra-ink truncate">{property.name || "Sin nombre"}</span>
                                </label>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-red-700 mt-2">{(form.restricted_property_ids || []).length} vivienda(s) restringida(s)</p>
                </Section>

                {canSeeFinancial && (
                <Section icon={Wallet} title="Nómina y banco">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="zentra-label flex items-center gap-1.5"><Banknote size={12} /> Salario bruto mensual (€)</label>
                            <input type="number" min="0" step="0.01" className="zentra-input" value={form.gross_salary_eur} onChange={(e) => setForm({ ...form, gross_salary_eur: e.target.value })} data-testid="employee-salary" />
                            <p className="text-[11px] text-zentra-muted mt-1">
                                Se sumará al coste total de personal en Contabilidad.
                            </p>
                        </div>
                        <div>
                            <label className="zentra-label">IBAN / Nº de cuenta</label>
                            <input className="zentra-input font-mono-zentra" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} placeholder="ES00 0000 0000 0000 0000 0000" />
                        </div>
                        <div className="col-span-2">
                            <label className="zentra-label">Notas internas</label>
                            <textarea className="zentra-textarea min-h-[60px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                </Section>
                )}

                {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
            </form>

            {isEdit && (
                <Section icon={KeyRound} title="Acceso al portal del operario">
                    <p className="text-xs text-zentra-muted mb-3">
                        Si creas un acceso, este empleado podrá entrar a ZENTRA con su propio email/contraseña y ver SOLO las tareas que tenga asignadas.
                    </p>
                    {loginUsers.length > 0 ? (
                        <div className="space-y-2">
                            {loginUsers.map((u) => (
                                <div key={u.id} className="flex items-center justify-between gap-2 p-2.5 bg-zentra-bg/40 border border-zentra-border rounded-md">
                                    <div className="min-w-0">
                                        <p className="text-sm font-mono-zentra text-zentra-ink truncate">{u.email}</p>
                                        <p className="text-[10px] text-zentra-muted uppercase tracking-wider">{u.role}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!window.confirm("¿Revocar el acceso de este usuario?")) return;
                                            await api.delete(`/employees/${emp.id}/login-access/${u.id}`);
                                            const r = await api.get(`/employees/${emp.id}/login-users`);
                                            setLoginUsers(r.data || []);
                                        }}
                                        className="text-[11px] text-red-700 hover:underline"
                                        data-testid={`revoke-emp-${u.id}`}
                                    >
                                        Revocar
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setShowLoginModal(true)}
                                className="zentra-btn-ghost text-xs"
                                data-testid="rotate-emp-password"
                            >
                                Rotar contraseña
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setShowLoginModal(true)}
                            className="zentra-btn-primary"
                            data-testid="create-emp-login"
                        >
                            <KeyRound size={14} /> Crear acceso de operario
                        </button>
                    )}
                </Section>
            )}

            <EmployeeLoginModal
                emp={emp}
                open={showLoginModal}
                existing={loginUsers[0]}
                onClose={() => setShowLoginModal(false)}
                onSaved={async () => {
                    const r = await api.get(`/employees/${emp.id}/login-users`);
                    setLoginUsers(r.data || []);
                }}
            />
        </Modal>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <fieldset className="border border-zentra-border rounded-lg p-4">
            <legend className="px-2 -ml-2 text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong inline-flex items-center gap-1.5">
                <Icon size={11} /> {title}
            </legend>
            {children}
        </fieldset>
    );
}

function EmployeeLoginModal({ emp, open, existing, onClose, onSaved }) {
    const [form, setForm] = useState({ email: "", role: "cleaner", password: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open && emp) {
            setForm({
                email: existing?.email || (emp.email || "").toLowerCase() || "",
                role: existing?.role || (emp.role === "technician" ? "technician" : "cleaner"),
                password: "",
            });
            setResult(null);
            setError("");
            setCopied(false);
        }
    }, [open, emp, existing]);

    if (!emp) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            const payload = { email: form.email.trim(), role: form.role };
            if (form.password.trim()) payload.password = form.password.trim();
            const { data } = await api.post(`/employees/${emp.id}/login-access`, payload);
            setResult(data);
            onSaved?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const copy = () => {
        const text = `Acceso ZENTRA — ${result.employee_name}\nURL: ${window.location.origin}/login\nEmail: ${result.email}\nContraseña: ${result.password}`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`Acceso operario · ${emp.name}`}
            subtitle="El empleado podrá entrar a su portal y ver SOLO sus tareas asignadas."
            wide
            testId="emp-login-modal"
            footer={
                result ? (
                    <button className="zentra-btn-primary" onClick={onClose} data-testid="emp-login-done">
                        Hecho
                    </button>
                ) : (
                    <>
                        <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                        <button type="submit" form="emp-login-form" className="zentra-btn-primary" disabled={saving} data-testid="emp-login-save">
                            {saving ? "Guardando…" : existing ? "Rotar contraseña" : "Crear acceso"}
                        </button>
                    </>
                )
            }
        >
            {result ? (
                <div className="space-y-4" data-testid="emp-login-result">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-sm font-semibold text-emerald-900 mb-3">
                            ✓ Acceso {result.action === "created" ? "creado" : "actualizado"}.
                        </p>
                        <div className="font-mono-zentra text-sm space-y-1.5">
                            <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">URL:</span>{window.location.origin}/login</div>
                            <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">Email:</span>{result.email}</div>
                            <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">Pass:</span>{result.password}</div>
                            <div><span className="text-zentra-muted text-xs uppercase tracking-wider mr-2">Rol:</span>{result.role}</div>
                        </div>
                    </div>
                    <button type="button" onClick={copy} className="zentra-btn-ghost" data-testid="emp-login-copy">
                        {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar credenciales</>}
                    </button>
                    <p className="text-xs text-zentra-muted">
                        Comparte estas credenciales por un canal seguro. La contraseña no se podrá volver a mostrar.
                    </p>
                </div>
            ) : (
                <form id="emp-login-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="zentra-label">Email de acceso</label>
                        <input
                            type="email"
                            className="zentra-input"
                            required
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            data-testid="emp-login-email"
                        />
                        <p className="text-[11px] text-zentra-muted mt-1">Si el email ya pertenece al empleado, se vincula automáticamente.</p>
                    </div>
                    <div>
                        <label className="zentra-label">Tipo de operario</label>
                        <select
                            className="zentra-select"
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            data-testid="emp-login-role"
                        >
                            <option value="cleaner">Limpieza</option>
                            <option value="technician">Técnico</option>
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Contraseña (vacío = auto)</label>
                        <input
                            className="zentra-input"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            placeholder="Mínimo 4 caracteres"
                            data-testid="emp-login-password"
                        />
                    </div>
                    {error && <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
                </form>
            )}
        </Modal>
    );
}

function ImportEmployeesModal({ open, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) {
            setFile(null);
            setPreview(null);
            setError("");
            setResult(null);
        }
    }, [open]);

    const buildAuthUrl = (path) => {
        const base = process.env.REACT_APP_BACKEND_URL;
        return `${base}/api${path}`;
    };

    const download = async (ext) => {
        try {
            const res = await api.get(`/employees/template.${ext}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = `zentra-plantilla-personal.${ext}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError(formatApiError(e));
        }
    };

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(null);
        setResult(null);
        setError("");
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const res = await api.post("/employees/import/preview", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(res.data);
        } catch (err) {
            setError(formatApiError(err));
            setFile(null);
        } finally {
            setLoading(false);
        }
    };

    const commit = async () => {
        if (!file) return;
        setLoading(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("preview_sha256", preview.file_sha256);
            const res = await api.post("/employees/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(res.data);
            onDone();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Importar personal desde Excel"
            subtitle="Sube un archivo .xlsx o .csv con tus trabajadores. ZENTRA detecta las cabeceras automáticamente."
            wide
            testId="import-employees-modal"
            footer={
                result ? (
                    <button className="zentra-btn-primary" onClick={onClose} data-testid="import-done-button">
                        Hecho
                    </button>
                ) : (
                    <>
                        <button className="zentra-btn-ghost" onClick={onClose}>Cancelar</button>
                        <button
                            type="button"
                            className="zentra-btn-primary"
                            onClick={commit}
                            disabled={!preview || (preview.errors?.length > 0) || preview.valid_count === 0 || loading}
                            data-testid="import-commit-button"
                        >
                            {loading ? "Importando…" : `Importar ${preview?.valid_count || 0} filas`}
                        </button>
                    </>
                )
            }
        >
            <div className="space-y-5">
                {result ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-2" data-testid="import-result">
                        <p className="font-semibold text-emerald-900">✓ Importación completada</p>
                        <ul className="list-disc list-inside text-emerald-900">
                            <li><strong>{result.created}</strong> creados</li>
                            <li><strong>{result.updated}</strong> actualizados</li>
                            <li><strong>{result.users_created}</strong> accesos creados</li>
                            <li><strong>{result.users_disabled}</strong> accesos desactivados</li>
                            {result.skipped > 0 && <li><strong>{result.skipped}</strong> omitidos por errores</li>}
                        </ul>
                        {result.temporary_credentials?.length > 0 && (
                            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-950">
                                <p className="font-semibold">Credenciales temporales · visualización única</p>
                                <p className="text-xs mb-2">Cópialas ahora. No se guardan en el lote ni volverán a mostrarse.</p>
                                <div className="max-h-44 overflow-y-auto font-mono-zentra text-xs space-y-1">
                                    {result.temporary_credentials.map((item) => <p key={item.employee_code}>{item.employee_code} · {item.email} · {item.temporary_password}</p>)}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="rounded-lg border border-zentra-border bg-zentra-bg/50 p-4">
                            <p className="text-sm font-semibold text-zentra-ink mb-2">
                                1 · Descarga la plantilla (opcional pero recomendado)
                            </p>
                            <p className="text-xs text-zentra-muted mb-3">
                                Trae las cabeceras correctas y ejemplos. Si ya tienes tu Excel, salta al paso 2.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="zentra-btn-ghost"
                                    onClick={() => download("xlsx")}
                                    data-testid="download-template-xlsx"
                                >
                                    <Download size={14} /> Plantilla .xlsx
                                </button>
                                <button
                                    type="button"
                                    className="zentra-btn-ghost"
                                    onClick={() => download("csv")}
                                    data-testid="download-template-csv"
                                >
                                    <Download size={14} /> Plantilla .csv
                                </button>
                            </div>
                        </div>

                        <div className="rounded-lg border border-zentra-border p-4">
                            <p className="text-sm font-semibold text-zentra-ink mb-2">2 · Sube tu archivo</p>
                            <input
                                type="file"
                                accept=".xlsx,.xls,.xlsm,.csv"
                                onChange={onPick}
                                className="block w-full text-sm text-zentra-ink file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-zentra-primary file:text-white hover:file:bg-zentra-primary/90 cursor-pointer"
                                data-testid="import-file-input"
                            />
                            {file && (
                                <p className="text-xs text-zentra-muted mt-2">
                                    Archivo: <span className="font-mono-zentra text-zentra-ink">{file.name}</span> · {(file.size / 1024).toFixed(1)} KB
                                </p>
                            )}
                            <p className="mt-3 text-xs text-zentra-muted">El proceso es idempotente por código de empleado y correo normalizado. DNI, IBAN y cumpleaños no se importan.</p>
                        </div>

                        {loading && !preview && (
                            <div className="zentra-skeleton h-24" />
                        )}

                        {preview && (
                            <div className="rounded-lg border border-zentra-border overflow-hidden" data-testid="import-preview">
                                <div className="bg-zentra-bg/50 px-4 py-2 border-b border-zentra-border flex items-center justify-between flex-wrap gap-2">
                                    <p className="text-sm font-semibold text-zentra-ink">3 · Vista previa</p>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span className="zentra-chip bg-emerald-100 text-emerald-900 border-emerald-300">
                                            {preview.valid_count} válidas
                                        </span>
                                        {preview.invalid_count > 0 && (
                                            <span className="zentra-chip bg-red-100 text-red-900 border-red-300">
                                                {preview.invalid_count} con errores
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {preview.errors?.length > 0 && (
                                    <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-800">
                                        {preview.errors.map((e, i) => <p key={i}>· {e}</p>)}
                                    </div>
                                )}

                                <div className="bg-white px-4 py-2 border-b border-zentra-border">
                                    <p className="text-[11px] font-semibold uppercase tracking-wider text-zentra-muted mb-1.5">
                                        Resumen anonimizado · {preview.summary?.active || 0} activos · {preview.summary?.inactive || 0} bajas · {preview.actions?.create_users || 0} accesos a crear
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(preview.mapping || []).map((m, i) => (
                                            <span
                                                key={i}
                                                className={`zentra-chip text-[10px] ${
                                                    m.field
                                                        ? "bg-zentra-accent/20 text-zentra-primary border-zentra-accent"
                                                        : "bg-zentra-bg text-zentra-muted border-zentra-border line-through"
                                                }`}
                                            >
                                                {m.col} {m.field ? `→ ${m.field}` : "(ignorada)"}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="max-h-80 overflow-y-auto">
                                    <table className="zentra-table text-xs">
                                        <thead className="sticky top-0 z-10">
                                            <tr>
                                                <th>#</th>
                                                <th>Bloque</th>
                                                <th>Código empleado</th>
                                                <th>Email</th>
                                                <th>Teléfono</th>
                                                <th>Dirección</th>
                                                <th>Incidencias</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(preview.rows || []).slice(0, 100).map((r) => (
                                                <tr
                                                    key={`${r.source_block}-${r.source_row}`}
                                                    className={r.issues?.length ? "bg-amber-50" : ""}
                                                >
                                                    <td className="font-mono-zentra text-zentra-muted">{r.source_row}</td>
                                                    <td>{r.source_block === "active" ? "Activo" : "Baja"}</td>
                                                    <td className="font-mono-zentra">{r.employee_code}</td>
                                                    <td>{r.has_email ? "Sí" : "No"}</td>
                                                    <td>{r.has_phone ? "Sí" : "No"}</td>
                                                    <td>{r.has_address ? "Sí" : "No"}</td>
                                                    <td>{r.issues?.join(", ") || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {(preview.rows || []).length > 100 && (
                                        <p className="text-[11px] text-zentra-muted text-center py-2">
                                            Mostrando 100 de {preview.rows.length} filas
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                                {error}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
